import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { images } from "~/assets";
import styles from "./ViewInvoice.module.scss";
import classNames from "classnames/bind";
import dayjs from "dayjs";
import { IInvoiceInViewInvoice } from "~/utils/interface/jobDetails";
import jobDetailsApi from "~/apis/jobDetails";
import { handleApiError } from "~/utils/helper";

const cx = classNames.bind(styles);

const ViewInvoice = () => {
  const { id } = useParams<{ id: string }>();
  // Customer link = 64-char token (no login, can pay); staff link = numeric id (login required)
  const isPublicLink = !!id && !/^\d+$/.test(id);
  const [searchParams] = useSearchParams();
  const justPaid = isPublicLink && searchParams.get("payment") === "success";
  const [invoice, setInvoice] = useState<IInvoiceInViewInvoice | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [reload, setReload] = useState(0);

  // Back from Stripe: the webhook may land a moment after the redirect, so refresh once more.
  useEffect(() => {
    if (!justPaid) return;
    const timer = setTimeout(() => setReload((n) => n + 1), 4000);
    return () => clearTimeout(timer);
  }, [justPaid]);

  const balanceDue = Number(invoice?.remaining) || 0;

  const handlePayNow = async () => {
    if (!id) return;
    setIsPaying(true);
    try {
      const response = await jobDetailsApi.createPublicCheckout(id);
      const checkoutUrl = response.data?.data?.checkout_url;
      if (!checkoutUrl) throw new Error("Failed to start the payment. Please try again.");
      window.location.href = checkoutUrl;
    } catch (error: any) {
      handleApiError(error?.message || "Failed to start the payment. Please try again.");
      setIsPaying(false);
    }
  };

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!id) return;

      try {
        if (!reload) setIsLoading(true);
        const response = isPublicLink
          ? await jobDetailsApi.getPublicInvoice(id)
          : await jobDetailsApi.getInvoiceInViewInvoice(id);
        setInvoice(response.data?.data);
      } catch (error: any) {
        handleApiError(
          error?.response?.data?.message || "Failed to fetch invoice"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [id, isPublicLink, reload]);

  const tableInformation = [
    { label: "Invoice", value: invoice?.invoiceUniqueCode || "-" },
    {
      label: "Date",
      value: `${dayjs(invoice?.invoiceDate).format("MM/DD/YYYY")}` || "-",
    },
    {
      label: "Amount Due",
      value: invoice?.remaining != null ? `$${Number(invoice.remaining).toFixed(2)}` : "-",
    },
    {
      label: "Due Date",
      value: `${dayjs(invoice?.dueDate).format("MM/DD/YYYY")}` || "-",
    },
  ];

  const subTotalTableInformation = [
    {
      label: "Sub Total",
      // invoices saved before tax support have sub_total = 0 -> fall back to total
      value: `$${(Number(invoice?.subTotal) || Number(invoice?.total || 0) - Number(invoice?.taxAmount || 0)).toFixed(2)}`,
    },
    {
      label: `Tax (${Number(invoice?.taxRate || 0)}%)`,
      value: `$${Number(invoice?.taxAmount || 0).toFixed(2)}`,
    },
    { label: "Total", value: `$${invoice?.total || "-"}` },
    {
      label: "Amount Paid",
      value: invoice ? `$${Number(invoice.paid || 0).toFixed(2)}` : "-",
    },
    {
      label: "Balance Due",
      value: invoice ? `$${balanceDue.toFixed(2)}` : "-",
    },
  ];

  if (isLoading) {
    return (
      <div className={cx("invoice-details-container")}>
        <div className={cx("header")}>
          <h3 className={cx("title")}>Loading...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className={cx("invoice-details-container")}>
      <div className={cx("header")}>
        <h3 className={cx("title")}>-- INVOICE --</h3>
      </div>

      <div className={cx("information-container")}>
        <div className={cx("information-row")}>
          <div className={cx("section-row")}>
            <div className={cx("item-row")}>
              <p className={cx("item-value")}>Christian Brothers Roofing</p>
              <p className={cx("item-value")}>11300 Decimal Drive,<br />
                Suite D <br />
                Louisville, KY 40299</p>
              <p className={cx("item-value")}>(502) 244-0208</p>
            </div>

            <div className={cx("item-row")}>
              <p className={cx("item-label")}>Sales Representative</p>
              <p className={cx("item-value")}>
                {invoice?.salesRepresentative?.name}
              </p>
              <p className={cx("item-value")}>
                {invoice?.salesRepresentative?.phone}
              </p>
              <p className={cx("item-value")}>
                {invoice?.salesRepresentative?.email}
              </p>
            </div>
          </div>

          <img src={images.logo} alt="Logo" className={cx("logo-image")} />
        </div>

        <div className={cx("information-row", "!flex !items-start")}>
          <div className={cx("section-item")}>
            <p className={cx("text-white")}>
              {invoice?.customer?.name || "N/A"}
            </p>
            <p className={cx("text-white")}>
              {invoice?.customer?.address || "N/A"}
            </p>
            <p className={cx("text-red")}>
              Insurance Co.: {invoice?.insuranceCompany?.name || "N/A"}
            </p>
            <p className={cx("text-red")}>
              Claim: {invoice?.insuranceCompany?.number || "N/A"}
            </p>
            <p className={cx("text-red", "!font-[400]")}>
              Email: {invoice?.insuranceCompany?.email}
            </p>
          </div>

          <div className={cx("table-wrapper")}>
            <div className={cx("table-information")}>
              {tableInformation.map((item) => (
                <div className={cx("table-information-row")}>
                  <div className={cx("table-information-label")}>
                    {item.label}
                  </div>
                  <div className={cx("table-information-value")}>
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={cx("sections-container")}>
        <div className={cx("table-wrapper")}>
          <table className={cx("sections-table")}>
            <thead>
              <tr>
                <th className={cx("col-item")}>
                  {invoice?.sections?.[0]?.sectionTitle || "Item"}
                </th>
                <th className={cx("col-quantity")}>Quantity</th>
                <th className={cx("col-price")}>Price</th>
                <th className={cx("col-amount")}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice?.sections?.map((section, sectionIndex) => (
                <React.Fragment key={sectionIndex}>
                  {sectionIndex > 0 && section.sectionTitle && (
                    <tr className={cx("section-header-row")}>
                      <td colSpan={4} className={cx("section-header")}>
                        {section.sectionTitle}
                      </td>
                    </tr>
                  )}
                  {section.lines?.map((line, lineIndex) => {
                    // Customer sees the marked-up unit price; amount = unit x qty (same as invoice totals).
                    const unit = (Number(line.price) || 0) * (1 + (Number(line.markup) || 0) / 100);
                    return (
                      <tr key={lineIndex} className={cx("item-row")}>
                        <td className={cx("col-item")}>{line.itemName}</td>
                        <td className={cx("col-quantity")}>{line.quantity}</td>
                        <td className={cx("col-price")}>${unit.toFixed(2)}</td>
                        <td className={cx("col-amount")}>${(unit * (Number(line.quantity) || 0)).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={cx("information-container", "!bg-transparent")}>
        <div className={cx("information-row", "!flex !items-start")}>
          <div className={cx("note-container")}>
            <p className={cx("note-text")}>{invoice?.customerNotes}</p>
          </div>

          <div className={cx("table-wrapper")}>
            <div className={cx("table-information")}>
              {subTotalTableInformation.map((item) => (
                <div className={cx("table-information-row")}>
                  <div className={cx("table-information-label")}>
                    {item.label}
                  </div>
                  <div className={cx("table-information-value")}>
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            {isPublicLink && (
              <div className={cx("pay-container")}>
                {justPaid && (
                  <p className={cx("pay-message")}>
                    Thank you, your payment was received. It may take a moment to show on this invoice.
                  </p>
                )}
                <button
                  type="button"
                  className={cx("pay-button")}
                  onClick={handlePayNow}
                  disabled={balanceDue <= 0 || isPaying}
                >
                  {balanceDue > 0
                    ? isPaying
                      ? "Redirecting..."
                      : `Pay $${balanceDue.toFixed(2)} now`
                    : "Paid in full"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewInvoice;
